import React, { useState } from "react";
import {
  Box,
  VStack,
  HStack,
  Input,
  Textarea,
  Select,
  FormControl,
  FormLabel,
  Checkbox,
  Stack,
  Switch,
  Button,
  Flex,
  Text,
  RadioGroup,
  Radio,
  Icon,
  InputGroup,
  InputLeftElement,
  Divider,
} from "@chakra-ui/react";
import {FiPlus, FiSearch, FiX, FiLock } from "react-icons/fi";
import { useNavigate } from "react-router-dom";
import { DarwinAgent, DarwinTool } from "build-ts/darwin_agent";
import { DarwinAgentType } from "build-ts/common";
import { FaReplyAll, FaTasks, FaTools } from "react-icons/fa";
import { RiRobotFill } from "react-icons/ri";

interface EvaAgentLeftPanelProps {
  agent: DarwinAgent;
  availableTools: DarwinTool[];
  onConfigChange: (field: string, value: any) => void;
  onToolsChange: (selectedToolsArray: DarwinTool[]) => void;
  isEditing?: boolean;
  onEdit?: () => void;
  onCancel?: () => void;
  onSave: () => void;
  isCreateMode?: boolean;
  onViewDocs?: () => void;
}

const EvaAgentLeftPanel: React.FC<EvaAgentLeftPanelProps> = ({
  agent,
  availableTools,
  onConfigChange,
  onToolsChange,
  isEditing = false,
  onEdit,
  onCancel,
  onSave,
  isCreateMode = false,
  onViewDocs,
}) => {
  const navigate = useNavigate();
  const [toolSearchTerm, setToolSearchTerm] = useState("");

  const handleBackToAgents = () => {
    navigate("/eva/agents");
  };

  return (
    <Box
      display="flex"
      flexDirection="column"
      height="100%"
      width="100%"
      minWidth={0}
      overflowY="auto"
      pr={2}
      css={{
        "&::-webkit-scrollbar": { width: "4px" },
        "&::-webkit-scrollbar-track": { background: "transparent" },
        "&::-webkit-scrollbar-thumb": { background: "#CBD5E0", borderRadius: "2px" },
      }}
    >
      <VStack spacing={4} align="stretch" flex="1" overflow="hidden">
        {/* Basic Agent Info */}
        <Box
          bg="white"
          px={4}
          py={4}
          borderRadius="12px"
          boxShadow="lg"
          display="flex"
          flexDirection="column"
          flex="1"
          overflow="hidden"
        >
          <Flex justify="space-between" align="center" mb={2}>
            <HStack>
              <Icon as={RiRobotFill} boxSize={6} color="purple.700" />
              <Text fontSize="lg" color="purple.700" fontWeight="700" mb={0}>
                Identity
              </Text>
            </HStack>
            {isCreateMode ? (
                <HStack spacing={2}>
                  <Button size="sm" variant="outline" onClick={onCancel}>
                    Cancel
                  </Button>
                  <Button
                    size="sm"
                    colorScheme="green"
                    onClick={onSave}
                    leftIcon={<Icon as={FiPlus} />}>
                    Create
                  </Button>
                </HStack>
              ) : !isEditing ? (
                <HStack spacing={2}>

                  <Button size="sm" colorScheme="purple"
                  bg="purple.700" color="white"
                  _hover={{ bg: "purple.600" }}
                  onClick={onEdit}
                  leftIcon={<Icon as={FiLock} />}>
                    Edit
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    leftIcon={<Icon as={FiX} />}
                    onClick={handleBackToAgents}
                    iconSpacing={0}
                  >
                  </Button>
                </HStack>
              ) : (
                <HStack spacing={2}>
                  <Button size="sm" variant="outline" onClick={onCancel}>
                    Cancel
                  </Button>
                  <Button
                    size="sm"
                    colorScheme="green"
                    onClick={onSave}
                  >
                    Save
                  </Button>
                </HStack>
              )}
          </Flex>
          <VStack spacing={4} align="stretch">
            <FormControl>
              <FormLabel>Name</FormLabel>
              <Input
                value={agent.header?.name || ""}
                onChange={(e) => onConfigChange("header.name", e.target.value)}
                placeholder="Enter agent name"
                isDisabled={!isEditing && !isCreateMode}
                color={!isEditing && !isCreateMode ? "gray.500" : "black"}
                fontSize="sm"
                fontWeight="600"
                bg={!isEditing && !isCreateMode ? "gray.50" : "white"}
              />
            </FormControl>

            <FormControl>
              <FormLabel>Description</FormLabel>
              <Textarea
                value={agent.header?.description || ""}
                onChange={(e) => onConfigChange("header.description", e.target.value)}
                placeholder="Briefly describe the agent's purpose"
                rows={3}
                isDisabled={!isEditing && !isCreateMode}
                resize="none"
                fontSize="sm"
                fontWeight="600"
                bg={!isEditing && !isCreateMode ? "gray.50" : "white"}
              />
            </FormControl>
            <HStack spacing={4} align="flex-start">
            <FormControl flex={2}>
              <FormLabel>Agent Type</FormLabel>
              {isCreateMode ? (
                <Select
                  value={agent.header?.type || ""}
                  onChange={(e) => onConfigChange("header.type", e.target.value)}
                  placeholder="Select agent type"
                  fontSize="sm"
                  fontWeight="600"
                  bg={!isEditing && !isCreateMode ? "gray.50" : "white"}
                >
                  <>
                    {Object.values(DarwinAgentType)
                      .filter(
                        value => typeof value === "string" && value !== "UNRECOGNIZED" && value !== "UNKNOWN_AGENT_TYPE"
                      )
                      .map((agentType) => (
                        <option key={agentType} value={agentType}>
                          {agentType}
                        </option>
                      ))}
                    </>
                </Select>
              ) : (
                <Input
                  value={agent.header?.type || ""}
                  readOnly
                  color="gray.600"
                  fontSize="sm"
                  fontWeight="600"
                  bg={!isEditing && !isCreateMode ? "gray.50" : "white"}
                />
              )}
            </FormControl>

            <FormControl flex={1}>
              <FormLabel>Is Active</FormLabel>
              <HStack>
                <Switch
                  isChecked={agent.header?.is_active || false}
                  onChange={(e) => onConfigChange("header.is_active", e.target.checked)}
                  colorScheme="blue"
                  isDisabled={!isEditing && !isCreateMode}
                />
              </HStack>
            </FormControl>
            </HStack>
          </VStack>
          <Divider />
          {/* Tools Selection Header & Search */}
          <VStack align="stretch" spacing={3} mt={0}>
            <HStack>
              <Icon as={FaTools} boxSize={5} color="purple.700" minW={6}/>
              <Text fontSize="lg" color="purple.700" fontWeight="700" mb={0}>
                 Toolkit
              </Text>
            </HStack>

            {/* Search Input */}
            <InputGroup size="sm">
              <InputLeftElement pointerEvents="none">
                <Icon as={FiSearch} color="gray.400" />
              </InputLeftElement>
              <Input
                placeholder="Search tools..."
                value={toolSearchTerm}
                onChange={(e) => setToolSearchTerm(e.target.value)}
                bg="white"
                borderColor="gray.300"
                _focus={{ borderColor: "purple.400", boxShadow: "0 0 0 1px purple.400" }}
              />
            </InputGroup>
          </VStack>

          {/* Tools Display - Split View */}
          {(() => {
            const searchLower = toolSearchTerm.toLowerCase();
            const regularTools = availableTools.filter(tool =>
              !tool.header?.is_response_tool &&
              (tool.header?.name?.toLowerCase().includes(searchLower) || !searchLower)
            );
            const responseTools = availableTools.filter(tool =>
              tool.header?.is_response_tool &&
              (tool.header?.name?.toLowerCase().includes(searchLower) || !searchLower)
            );

            return (
              <VStack align="stretch" spacing={3} flex="1" mt={3} overflow="hidden">
                {/* Task Tools Section - 65% */}
                <Box flex="3" overflow="hidden" display="flex" flexDirection="column" minHeight={0}>
                  <HStack mb={2} align="center" bg="blue.50" p={1} borderRadius="md">
                    <Icon as={FaTasks} boxSize={4} color="blue.600" />
                    <Text
                      fontSize="sm"
                      fontWeight="bold"
                      color="blue.600"
                      textAlign="left"
                      mb={0}
                    >
                      Task Completion Tools ({regularTools.length})
                    </Text>
                  </HStack>
                  <Box
                    flex="1"
                    overflowY="auto"
                    overflowX="hidden"
                    px={2}
                    minHeight={0}
                    css={{
                      "&::-webkit-scrollbar": { width: "4px" },
                      "&::-webkit-scrollbar-track": { background: "transparent" },
                      "&::-webkit-scrollbar-thumb": { background: "#CBD5E0", borderRadius: "2px" },
                    }}
                  >
                    {regularTools.length > 0 ? (
                      <Stack spacing={1} width="100%">
                        {regularTools.map((tool) => {
                          const toolId = tool.header?.id;
                          const toolName = tool.header?.name;
                          const isChecked = agent.tools?.some(selectedTool => {
                            const selectedToolId = selectedTool.header?.id;
                            return selectedToolId === toolId;
                          }) || false;

                          return (
                            <Checkbox
                              key={toolId}
                              id={toolId}
                              value={toolId}
                              isChecked={isChecked}
                              colorScheme="blue"
                              size="sm"
                              isDisabled={!isEditing && !isCreateMode}
                              alignItems="flex-start"
                              sx={{
                                ".chakra-checkbox__control": {
                                  marginTop: "2px"
                                }
                              }}
                              onChange={(e) => {
                                const isChecked = e.target.checked;
                                const currentTools = (agent.tools || []) as DarwinTool[];
                                if (isChecked) {
                                  const next = [...currentTools, tool] as DarwinTool[];
                                  onToolsChange(next as any);
                                } else {
                                  const filtered = currentTools.filter(currentTool => {
                                    const currentToolId = currentTool.header?.id;
                                    return currentToolId !== toolId;
                                  });
                                  onToolsChange(filtered as any);
                                }
                              }}
                            >
                              <Text
                                fontSize="sm"
                                fontWeight="600"
                                mb={0}
                                wordBreak="break-word"
                                whiteSpace="normal"
                                maxW="100%"
                                textAlign="left"
                                lineHeight="1.2"
                              >
                                {toolName}
                              </Text>
                            </Checkbox>
                          );
                        })}
                      </Stack>
                    ) : (
                      <Text fontSize="xs" color="gray.500">No tools found</Text>
                    )}
                  </Box>
                </Box>

                {/* Response Tools Section - 35% */}
                {responseTools.length > 0 && (
                  <Box flex="2" overflow="hidden" display="flex" flexDirection="column" borderTop="1px solid" borderColor="gray.200" pt={2} minHeight={0}>
                    <HStack mb={2} align="center" bg="green.50" p={1} borderRadius="md">
                      <Icon as={FaReplyAll} boxSize={4} color="green.700" />
                      <Text
                        fontSize="sm"
                        fontWeight="bold"
                        color="green.700"
                        textAlign="left"
                        mb={0}
                      >
                        Response Tools ({responseTools.length})
                      </Text>
                    </HStack>
                    <Box
                      flex="1"
                      overflowY="auto"
                      overflowX="hidden"
                      px={2}
                      minHeight={0}
                      css={{
                        "&::-webkit-scrollbar": { width: "4px" },
                        "&::-webkit-scrollbar-track": { background: "transparent" },
                        "&::-webkit-scrollbar-thumb": { background: "#CBD5E0", borderRadius: "2px" },
                      }}
                    >
                      <RadioGroup
                        value={(() => {
                          const selectedResponseTool = agent.tools?.find(selectedTool => {
                            return responseTools.some(responseTool =>
                              responseTool.header?.id === selectedTool.header?.id
                            );
                          });
                          return selectedResponseTool?.header?.id || "";
                        })()}
                        onChange={(value) => {
                          const currentTools = (agent.tools || []) as DarwinTool[];
                          const filteredTools = currentTools.filter(currentTool => {
                            return !responseTools.some(responseTool =>
                              responseTool.header?.id === currentTool.header?.id
                            );
                          });

                          if (value) {
                            const selectedTool = responseTools.find(tool => tool.header?.id === value);
                            if (selectedTool) {
                              const next = [...filteredTools, selectedTool] as DarwinTool[];
                              onToolsChange(next as any);
                            }
                          } else {
                            onToolsChange(filteredTools as any);
                          }
                        }}
                        isDisabled={!isEditing && !isCreateMode}
                      >
                        <Stack spacing={0} width="100%">
                          {responseTools.map((tool) => {
                            const toolId = tool.header?.id;
                            const toolName = tool.header?.name;
                            const isSelected = agent.tools?.some(selectedTool => {
                              return selectedTool.header?.id === toolId;
                            }) || false;

                            return (
                              <Box
                                key={toolId}
                                onClick={(e) => {
                                  if (isSelected && (isEditing || isCreateMode)) {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    const currentTools = (agent.tools || []) as DarwinTool[];
                                    const filteredTools = currentTools.filter(currentTool => {
                                      return currentTool.header?.id !== toolId;
                                    });
                                    onToolsChange(filteredTools as any);
                                  }
                                }}
                                cursor={isSelected && (isEditing || isCreateMode) ? "pointer" : "default"}
                                textAlign="left"
                              >
                                <Radio
                                  value={toolId}
                                  colorScheme="green"
                                  size="sm"
                                  alignItems="center"
                                >
                                  <Text
                                    fontSize="sm"
                                    fontWeight="600"
                                    mb={0}
                                    wordBreak="break-word"
                                    whiteSpace="normal"
                                    maxW="100%"
                                    textAlign="left"
                                    lineHeight="1.2"
                                  >
                                    {toolName}
                                  </Text>
                                </Radio>
                              </Box>
                            );
                          })}
                        </Stack>
                      </RadioGroup>
                    </Box>
                  </Box>
                )}
              </VStack>
            );
          })()}
        </Box>
      </VStack>
    </Box>
  );
};

export default EvaAgentLeftPanel;
